// src/modules/admin/pages/AdminExemptions/index.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Badge, Alert, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { useService } from '../../../../shared/hooks/useService';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import Loading from '../../../../shared/components/Loading';
import ErrorState from '../../../../shared/components/ErrorState';
import ExemptionCard from '../../components/ExemptionCard';
import './styles.css';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'all'];

const STATUS_LABELS = {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    all: 'All',
};

const AdminExemptions = () => {
    const service = useService();
    const { hasPermission } = useAuth();
    const hasExemptionPermission = hasPermission('activities:handleExemptions');
    const [exemptions, setExemptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [hasMore, setHasMore] = useState(false);
    const [status, setStatus] = useState('pending');
    const [error, setError] = useState(null);
    const [exemptionCounts, setExemptionCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const { isWarmup } = useWarmupTimer(isLoading);
    const loadMoreRef = useRef(null);

    // Refs for stable access inside callbacks
    const nextTokenRef = useRef(null);
    const isLoadingMoreRef = useRef(false);
    const abortRef = useRef(0);

    const fetchPage = useCallback(async (append, token, abortId) => {
        try {
            const result = await service.getAdminExemptions({
                status: status === 'all' ? undefined : status,
                pageSize: PAGE_SIZE,
                nextToken: token,
            });

            if (abortRef.current !== abortId) return;

            if (append) {
                setExemptions(prev => [...prev, ...result.exemptions]);
            } else {
                setExemptions(result.exemptions);
            }

            nextTokenRef.current = result.nextToken || null;
            setHasMore(!!result.nextToken);
        } catch (err) {
            if (abortRef.current !== abortId) return;
            setError(err.message || 'Failed to fetch exemptions');
        } finally {
            if (abortRef.current !== abortId) return;
            setIsLoading(false);
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
        }
    }, [service, status]);

    // Fetch exemption counts from backend on mount
    useEffect(() => {
        service.getExemptionCounts().then(setExemptionCounts).catch(() => {
            // Ignore errors for counts — fall back to zeros
        });
    }, [service]);

    // Reset and load first page when status changes
    useEffect(() => {
        const abortId = ++abortRef.current;
        nextTokenRef.current = null;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
        setHasMore(false);
        setIsLoading(true);
        setError(null);
        fetchPage(false, null, abortId);
    }, [status, fetchPage]);

    // IntersectionObserver for infinite scroll
    useEffect(() => {
        if (isLoading) return;

        const observer = new IntersectionObserver(
            entries => {
                if (
                    entries[0].isIntersecting &&
                    nextTokenRef.current &&
                    !isLoadingMoreRef.current
                ) {
                    const abortId = abortRef.current;
                    isLoadingMoreRef.current = true;
                    setIsLoadingMore(true);
                    fetchPage(true, nextTokenRef.current, abortId);
                }
            },
            { rootMargin: '200px' }
        );

        const currentRef = loadMoreRef.current;
        if (currentRef) observer.observe(currentRef);

        return () => observer.disconnect();
    }, [isLoading, fetchPage]);

    const handleReview = async (activityId, action, note) => {
        await service.reviewExemption(activityId, { action, note });
        // Refetch counts so tab badges update after a review
        service.getExemptionCounts().then(setExemptionCounts).catch(() => {});
        // Refetch the list to get updated data
        const abortId = ++abortRef.current;
        nextTokenRef.current = null;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
        setHasMore(false);
        setIsLoading(true);
        setError(null);
        fetchPage(false, null, abortId);
    };

    const handleStatusChange = (newStatus) => {
        setStatus(newStatus);
    };

    // Counts come from the backend API, not from the currently loaded page
    const counts = {
        pending: exemptionCounts.pending,
        approved: exemptionCounts.approved,
        rejected: exemptionCounts.rejected,
    };

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (!hasExemptionPermission) {
        return (
            <Container className="admin-exemptions-container">
                <Alert variant="warning" className="admin-no-permission-alert">
                    <Alert.Heading>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No Permission
                    </Alert.Heading>
                    <p>You need the <strong>activities:handleExemptions</strong> permission to view and review exemption requests.</p>
                    <Button variant="outline-warning" size="sm" as={Link} to="/admin">
                        <i className="bi bi-arrow-left me-1"></i>
                        Back to Dashboard
                    </Button>
                </Alert>
            </Container>
        );
    }
    if (error) return <ErrorState message={error} onRetry={() => { setStatus(status); }} />;

    return (
        <Container className="admin-exemptions-container">
            {/* Status filter tabs */}
            <div className="admin-exemptions-tabs">
                {STATUS_OPTIONS.map(option => {
                    const label = STATUS_LABELS[option];
                    const count = option === 'all'
                        ? exemptionCounts.pending + exemptionCounts.approved + exemptionCounts.rejected
                        : counts[option];
                    const isActive = status === option;

                    return (
                        <button
                            key={option}
                            className={`admin-exemptions-tab ${isActive ? 'active' : ''}`}
                            onClick={() => handleStatusChange(option)}
                            disabled={isActive}
                        >
                            {label}
                            <Badge bg={isActive ? 'primary' : 'secondary'} className="admin-exemptions-count">
                                {count}
                            </Badge>
                        </button>
                    );
                })}
            </div>

            {/* Exemption cards list */}
            {exemptions.length > 0 ? (
                <div className="admin-exemptions-list">
                    {exemptions.map(exemption => (
                        <ExemptionCard
                            key={exemption.activityId}
                            exemption={exemption}
                            onReview={handleReview}
                        />
                    ))}
                </div>
            ) : (
                <div className="admin-exemptions-empty">
                    <div className="admin-exemptions-empty-icon">🛡️</div>
                    <h3>No exemptions found</h3>
                    <p>
                        {status === 'all'
                            ? 'There are no exemption requests yet.'
                            : `No ${STATUS_LABELS[status].toLowerCase()} exemption requests.`}
                    </p>
                </div>
            )}

            {/* Load more / infinite scroll sentinel */}
            <div className="admin-exemptions-load-more" ref={loadMoreRef}>
                {isLoadingMore && (
                    <div className="admin-exemptions-loading">
                        <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        Loading more exemptions…
                    </div>
                )}
            </div>
        </Container>
    );
};

export default AdminExemptions;
